import React, { useEffect, useState } from "react";
import { unauthAxiosFetcher } from "../axios";
import {
  mongoliaDestinationReasons,
  japowDestinationReasons,
  japowWhyUsReasons,
  japowReviews,
  japowFaqs,
  // Add other destination constants as needed
} from "../constants/destination";
import { ProjectBox } from "../home/ProjectBox";
import {
  FAQs,
  Footer,
  HeroFeatured,
  StickyHeader,
  WhyChooseUs,
} from "./components/landing-new";

// Configuration object for different destinations
const destinationConfig = {
  mongolia: {
    country: "Mongolia",
    videos: {
      desktop:
        "https://d1puf2nnbcyl9q.cloudfront.net/mongolia_landing_vid_desktop.mov",
      mobile:
        "https://d1puf2nnbcyl9q.cloudfront.net/mongolia_landing_vid_mobile.mov",
    },
    cta: {
      text: "Browse Mongolia trips",
      scrollTarget: "destination-tours",
    },
    title: "Mongolia tours",
    whyDestinationTitle: "Why choose Mongolia?",
    whyDestinationDescription:
      "Hear from those who have visited this incredible destination.",
    destinationReasons: mongoliaDestinationReasons,
  },
  japow: {
    country: "Japan",
    broad_category: "snow",
    videos: {
      desktop: "https://d1puf2nnbcyl9q.cloudfront.net/japow-25-desktop.mp4",
      mobile: "https://d1puf2nnbcyl9q.cloudfront.net/japow-25-mobile.mp4",
    },
    cta: {
      text: "Explore Japow adventures",
      scrollTarget: "destination-tours",
    },
    title: "Japow experiences",
    whyDestinationTitle: "Why visit Japow?",
    whyDestinationDescription:
      "Discover what makes Japow an unforgettable destination.",
    destinationReasons: japowDestinationReasons,

    whyUsTitle: "Shred with our crew",
    whyUsDescription:
      "From first chair to last - ride, level up, laugh, and make some core memories.",
    whyUsReasons: japowWhyUsReasons,

    reviewsTitle: "Hear from past seasons",
    reviewsDescription: "Unfiltered reasons why our community is hooked!",
    reviewsReasons: japowReviews,
    customFaqs: japowFaqs,
  },
};

const Destination = ({ destinationKey = "mongolia" }) => {
  const config = destinationConfig[destinationKey.toLowerCase()];

  if (!config) {
    console.error(`Destination config not found for: ${destinationKey}`);
    return <div>Destination not found</div>;
  }

  return (
    <>
      <StickyHeader />
      <HeroFeatured
        desktopVideo={config.videos.desktop}
        mobileVideo={config.videos.mobile}
        ctaText={config.cta.text}
        onCtaClick={() => {
          document.getElementById(config.cta.scrollTarget).scrollIntoView({
            behavior: "smooth",
          });
        }}
      />
      <WhyChooseUs
        className="tw-mt-12"
        mobileVariant="horizontal"
        title={config.whyDestinationTitle}
        description={config.whyDestinationDescription}
        reasons={config.destinationReasons}
        removeCta={true}
      />

      {config.whyUsTitle && (
        <WhyChooseUs
          title={config.whyUsTitle}
          description={config.whyUsDescription}
          reasons={config.whyUsReasons}
          cta={() => {
            document.getElementById(config.cta.scrollTarget).scrollIntoView({
              behavior: "smooth",
            });
          }}
        />
      )}

      {config.reviewsTitle && (
        <WhyChooseUs
          mobileVariant="horizontal"
          title={config.reviewsTitle}
          description={config.reviewsDescription}
          reasons={config.reviewsReasons}
          removeCta={true}
        />
      )}

      <Tours destinationKey={destinationKey} config={config} />
      <FAQs customFaqs={config.customFaqs} />
      <Footer />
    </>
  );
};

const Tours = ({ destinationKey, config }) => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTours = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await unauthAxiosFetcher(
          `/v1/project/getProjectsByCountry?country=${config.country}${
            config.broad_category
              ? `&broad_category=${config.broad_category}`
              : ""
          }`
        );

        const formattedTours = data.map((tour) => ({
          id: tour.id,
          project_id: tour.id.toString(),
          name: tour.project_name,
          main_images: tour.main_images,
          price: tour.price,
          skill_level: tour.skill_level || "Beginner",
          duration: tour.duration,
          country: tour.country,
          city: tour.city,
          highlights:
            tour.highlights?.map((highlight) => ({
              key: highlight.highlight_key,
              description: highlight.description,
            })) || [],
          three_user_images: tour.three_user_images,
        }));

        setTours(formattedTours);
      } catch (err) {
        console.error(`Error fetching tours for ${config.country}:`, err);
        setError(`Failed to load ${config.country} tours`);
      } finally {
        setLoading(false);
      }
    };

    fetchTours();
  }, [destinationKey, config.country]);

  if (loading) {
    return (
      <div
        className="tw-px-4 md:tw-px-16 tw-py-4 md:tw-py-16"
        id="destination-tours"
      >
        <div className="tw-text-[26px] md:tw-text-[32px] tw-font-extrabold tw-mb-4">
          Loading {config.title}...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="tw-px-4 md:tw-px-16 tw-py-4 md:tw-py-16"
        id="destination-tours"
      >
        <div className="tw-text-[26px] md:tw-text-[32px] tw-font-extrabold tw-mb-4">
          {config.title}
        </div>
        <div className="tw-text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div
      className="tw-px-4 md:tw-px-16 tw-py-4 md:tw-py-16"
      id="destination-tours"
    >
      <div className="tw-text-[26px] md:tw-text-[32px] tw-font-extrabold tw-mb-4">
        {config.title}
      </div>
      {tours.length === 0 ? (
        <div className="tw-text-gray-500">
          No tours available for {config.country} at the moment.
        </div>
      ) : (
        <div className="tw-grid md:tw-grid-cols-2 tw-gap-6 HomeContainer">
          {tours.map((tour) => (
            <ProjectBox
              key={tour.id}
              {...tour}
              boxStyle={{
                margin: "0",
                alignItems: "flex-start",
              }}
              containerStyle={{
                alignItems: "flex-start",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Destination;
